import React from "react";
import InputField from "../input-field/input-field";
import {
  SelectInput,
  SelectInputOption,
} from "../select-input/select-input";
import { Tags, TagOption } from "../tags/tags";
import { TrashIcon } from "../../SVG/trash-icon";
import "./editable-table.scss";

export type EditableTableColumnType =
  | "input"
  | "select"
  | "textarea"
  | "tag";

export interface EditableTableColumn {
  key: string;
  header: string;
  type: EditableTableColumnType;
  /** Fixed column width (e.g. "8.125rem"). Omit to flex-fill remaining space. */
  width?: string;
  placeholder?: string;
  /** Required when type is "select". */
  options?: SelectInputOption[];
  /** Number of visible text rows. Only applies when type is "textarea". @default 2 */
  rows?: number;
  /**
   * Value-to-display lookup used when type is "tag". Each entry provides the
   * label and styling (background, text color, border) to render for a value.
   * If the cell value has no matching entry, the raw value is shown as the
   * label using `fallbackTagStyle` (or a neutral gray default).
   */
  tagOptions?: TagOption[];
  /**
   * Styling applied when a tag cell's value has no matching `tagOptions` entry.
   * Defaults to a neutral gray tag.
   */
  fallbackTagStyle?: Pick<TagOption, "backgroundColor" | "textColor" | "showBorder">;
}

const DEFAULT_FALLBACK_TAG_STYLE: Pick<
  TagOption,
  "backgroundColor" | "textColor" | "showBorder"
> = {
  backgroundColor: "#f5f5f5",
  textColor: "#5c5c5c",
  showBorder: false,
};

export interface EditableTableRow {
  id: string;
  [field: string]: string;
}

export interface EditableTableProps {
  columns: EditableTableColumn[];
  items: EditableTableRow[];
  onItemChange: (id: string, field: string, value: string) => void;
  onItemRemove: (id: string) => void;
  onAddItem?: () => void;
  addLabel?: string;
  className?: string;
  /** Applied to every input and select element in the table rows. Useful for controlling height. */
  inputClassName?: string;
}

const cls = "editable-table";

function PlusIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M7 1v12M1 7h12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function getColumnStyle(column: EditableTableColumn): React.CSSProperties {
  if (column.width) {
    return { width: column.width, flexShrink: 0 };
  }
  return { flex: 1, minWidth: 0 };
}

export const EditableTable: React.FC<EditableTableProps> = ({
  columns,
  items,
  onItemChange,
  onItemRemove,
  onAddItem,
  addLabel = "Add Item",
  className,
  inputClassName,
}) => {
  return (
    <div className={[cls, className].filter(Boolean).join(" ")}>
      <div className={`${cls}__table`}>
        <div className={`${cls}__header`}>
          {columns.map((column) => (
            <span
              key={column.key}
              className={`${cls}__header-cell`}
              style={getColumnStyle(column)}
            >
              {column.header}
            </span>
          ))}
        </div>

        <div className={`${cls}__body`}>
          {items.map((item) => (
            <div key={item.id} className={`${cls}__row`}>
              {columns.map((column) => {
                const value = item[column.key] ?? "";
                return (
                  <div
                    key={column.key}
                    className={`${cls}__cell`}
                    style={getColumnStyle(column)}
                  >
                    {column.type === "input" && (
                      <InputField
                        size="sm"
                        placeholder={column.placeholder}
                        value={value}
                        onChange={(e) =>
                          onItemChange(item.id, column.key, e.target.value)
                        }
                        fullWidth
                        inputClassName={inputClassName}
                      />
                    )}
                    {column.type === "select" && (
                      <SelectInput
                        placeholder={column.placeholder}
                        options={column.options ?? []}
                        value={value}
                        onChange={(val) =>
                          onItemChange(item.id, column.key, val)
                        }
                        inputClassName={inputClassName}
                      />
                    )}
                    {column.type === "textarea" && (
                      <textarea
                        className={[`${cls}__textarea`, inputClassName]
                          .filter(Boolean)
                          .join(" ")}
                        placeholder={column.placeholder}
                        value={value}
                        rows={column.rows ?? 1}
                        onChange={(e) =>
                          onItemChange(item.id, column.key, e.target.value)
                        }
                      />
                    )}
                    {column.type === "tag" && (() => {
                      const matched = column.tagOptions?.find(
                        (opt) => opt.value === value
                      );
                      const fallback =
                        column.fallbackTagStyle ?? DEFAULT_FALLBACK_TAG_STYLE;
                      return (
                        <Tags
                          label={matched?.label ?? value}
                          backgroundColor={
                            matched?.backgroundColor ?? fallback.backgroundColor
                          }
                          textColor={matched?.textColor ?? fallback.textColor}
                          showBorder={
                            matched?.showBorder ?? fallback.showBorder
                          }
                          className={`${cls}__tag`}
                        />
                      );
                    })()}
                  </div>
                );
              })}
              <button
                type="button"
                className={`${cls}__remove-btn`}
                onClick={() => onItemRemove(item.id)}
                aria-label="Remove row"
              >
                <TrashIcon size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {onAddItem && (
        <button
          type="button"
          className={`${cls}__add-btn`}
          onClick={onAddItem}
        >
          <PlusIcon />
          {addLabel}
        </button>
      )}
    </div>
  );
};

export default EditableTable;
